import { ActivityFilterKey, useWorkspaceAsset } from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  SlideAction,
  SlideLayout,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { Issue } from '../../issues/components/issue';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider } from '../assetRouteContext';
import { ALL_ACTIVITIES_QUERY_KEY, RoutineSubmitSource } from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';

export const AssetActivity = () => {
  const { assetId, issueId: contextIssueId, issueId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const [showFilters, setShowFilters] = useState(false);

  if (!assetId) {
    return null;
  }

  const from: RoutineSubmitSource | undefined = location.state?.from;
  const cameFromAsset = from === RoutineSubmitSource.AssetCard;

  const leftColumn = {
    title: 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    mainComponent: cameFromAsset
      ? (
          <AssetCard assetId={assetId} forceShowing />
      )
      : <LeftColumn assetId={assetId} selectedIssue={issueId} />,
    headerComponent: cameFromAsset
      ? <AssetFilters assetId={assetId} />
      : <ActivityFilters onFilterChange={() => setShowFilters(!showFilters)} />,
    showBackButton: true,
    disableScroll: cameFromAsset ? false : true,
    // When left shows AssetCard (cameFromAsset), go all the way to the root list.
    // When left shows ActivityList, go up to the asset home so the list stays in context.
    onBackButtonPress: () => navigate('../..', { relative: 'path' }),
    backButtonTransition: SlideAction.PREV,
  };

  const rightColumn = {
    title: 'assets.menu.title',
    showHeader: false,
    disableScroll: true,
    mainComponent: <Issue showAssetLink={false} />,
  };
  
  // Filter panel lives in the third column so the activity detail (rightColumn)
  // keeps its scroll position when the user opens and closes the filter panel.
  // Toggling showFilters to false from onBackButtonPress lets React re-derive
  // thirdColumn as undefined on the next render, which collapses the panel.
  const thirdColumn = showFilters
    ? {
        title: 'assets.menu.title',
        mainComponent: (
          <ActivityFilterScreen
            ref={filterRef}
            mode="page"
            isVisible={true}
            filterKey={ActivityFilterKey.AssetActivity}
            assetId={assetId}
            bottomBar={() => <></>}
          />
        ),
        headerComponent: (
          <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
        ),
        showBackButton: true,
        onBackButtonPress: () => setShowFilters(false),
        disableScroll: true,
      }
    : undefined;

  return (
    <AssetRouteProvider value={{
      basePath: contextIssueId ? `/activities/${contextIssueId}/asset/${assetId}` : `/assets/${assetId}`,
      listPath: contextIssueId ? '/activities' : '/assets',
      isWorkspaceMode: false,
      issueId: undefined,
    }}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        viewMode={ViewMode.DUAL}
      />
    </AssetRouteProvider>
  );
};

const LeftColumn = ({
  assetId,
  selectedIssue,
}: {
  assetId: string;
  selectedIssue?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const onIssueSelected = (issueId: string) => {
    // When inside an activities route, preserve the full context prefix so the
    // selected activity stays within the activities chain. We read the outer
    // activity ID directly from the URL because useParams() can't reliably
    // surface it (the assetSubRoutes spread reuses :issueId for both the outer
    // and inner position, making the outer ID inaccessible via params).
    if (location.pathname.startsWith('/activities/')) {
      const assetSegment = `/asset/${assetId}`;
      const prefix = location.pathname.split(assetSegment)[0];
      navigate(`${prefix}${assetSegment}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`);
    } else {
      navigate(`/assets/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`);
    }
  };

  return (
    <ActivityList
      filterKey={ActivityFilterKey.AssetActivity}
      onIssueSelected={onIssueSelected}
      assetId={assetId}
      showAssetName={false}
      showImage={false}
      pageSize={50}
      selectedIssue={selectedIssue}
    />
  );
};
